import { Token } from "@/components/token";
import { WithErrorAndLoading } from "@/components/with-error-and-loading";
import {
  toLocaleString,
  formatNumber, showTimeDiffPartial
} from "@/lib/utils";
import {
  DSContractState
} from "@myth-finance/dualstake-ts-sdk";
import { useMemo, useState } from "react";
import { cx } from "class-variance-authority";
import {
  RefreshCw, UsersRound
} from "lucide-react";
import {
  AccountLink
} from "@/components/links";
import { Button } from "@/components/ui/button";
import { getHighlightClasses } from "@/lib/get-tokens-highlights";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { ColumnDef } from "@tanstack/react-table";
import { DataTable } from "@/components/data-table";
import { DataTableColumnHeader } from "@/components/data-column-header";
import { useQuery } from "@tanstack/react-query";
import { indexer } from "@/lib/algo";
import { getApplicationAddress, indexerModels } from "algosdk";
  

type Holder = {
  account: string;
  balance: number;
};

const defaultHoldersSort = [
  { id: "balance", desc: true },
  { id: "account", desc: false },
];

export function HoldersDialog({
  cState,
  className,
}: {
  cState: DSContractState;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const highlightClass = useMemo(
    () => getHighlightClasses(cState.asaId, "text"),
    [cState.asaId]
  );

  const queryKey = ["holders", Number(cState.lstId)];

  const {
    isPending,
    error,
    data: holders,
    dataUpdatedAt,
    isFetching,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: async (): Promise<Holder[] | null> => {
      let nextToken = "";
      const holders: Holder[] = [];
      do {
        const escrow = getApplicationAddress(cState.appId);
        const response = new indexerModels.AssetBalancesResponse(
          // @ts-ignore
          await indexer.lookupAssetBalances(Number(cState.lstId)).do()
        );
        holders.push(
          ...response.balances
            .filter(({ address: a }) => a !== escrow)
            .map(({ address: a, amount: b }) => ({
              account: a,
              balance: Number(b),
            }))
        );
        nextToken = response.nextToken ?? "";
      } while (nextToken);
      return holders;
    },
    enabled: open, // first query when opened
    gcTime: 1000 * 60 * 60, // 1 hour
    staleTime: 1000 * 60 * 10, // 10 minutes
  });

  const [numHolders, numOptins, totalCirc]  = useMemo(() => {
    const numHolders = holders?.filter(({ balance }) => balance > 0).length;
    const totalCirc = holders?.reduce((totalCirc, { balance: b}) => totalCirc + b, 0)
    return [numHolders ?? 0, holders?.length ?? 0, totalCirc ?? 0];
  }, [holders]);

  const lastUpdatedAt = useMemo(() => {
    const diff = Date.now() - dataUpdatedAt;
    if (diff < 1000) return "just now";
    return showTimeDiffPartial(diff, 1) + " ago";
  }, [dataUpdatedAt, open]);

  const columns: ColumnDef<Holder>[] = [
    {
      accessorKey: "account",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Account"
        />
      ),
      cell: ({ row }) => (
        <div>
          <AccountLink address={row.getValue("account")} />
        </div>
      ),
    },
    {
      accessorKey: "balance",
      header: ({ column }) => (
        <DataTableColumnHeader
          className="justify-end"
          column={column}
          title="Balance"
        />
      ),
      cell: ({ row }) => {
        const amount = parseFloat(row.getValue("balance"));
        return (
          <div className="text-right font-medium">
            {formatNumber(amount, 6)} {cState.lstName}
          </div>
        );
      },
    },
  ];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <div className={cx("text-muted-foreground/90 self-start", className)}>
          <Button variant="ghost" className="text-base">
            <UsersRound className="h-4 w-4 -ml-1 mr-1 group-data-[state=open]:hidden" />
            Holders
          </Button>
        </div>
      </DialogTrigger>

      <DialogContent className="flex flex-col max-h-[100svh] overflow-y-auto">
        <DialogTitle className="text-center">
          <span className={highlightClass}>{cState.lstName}</span> Holders
        </DialogTitle>
        <div className="flex w-full justify-around items-center px-4">
          {isPending ? null : (
            <div>
              <span className={highlightClass}>
                {toLocaleString(numHolders)}
              </span>{" "}
              Holders
            </div>
          )}
          <Token
            glowing={false}
            variant="sm"
            leftAssetId={Number(cState.asaId)}
            rightAssetId={0}
          />
          {isPending ? null : (
            <div className="text-muted-foreground">
              {toLocaleString(numOptins)} Optins
            </div>
          )}
        </div>
        <WithErrorAndLoading
          mini={true}
          loading={isPending}
          loadingClassName="my-8"
          error={error?.message}
        >
          <div className="flex flex-col gap-2">
            <DataTable
              columns={columns}
              data={holders ?? []}
              defaultSort={defaultHoldersSort}
            />
            <div className="text-muted-foreground flex justify-between items-center text-sm">
              <div>Circulation {formatNumber(totalCirc, 6, 2)}</div>
              <div className="flex items-center">
                <p>Updated {lastUpdatedAt}</p>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => refetch()}
                  disabled={isFetching}
                >
                  {isFetching ? (
                    <RefreshCw className="animate-spin" />
                  ) : (
                    <RefreshCw />
                  )}
                </Button>
              </div>
            </div>
          </div>
        </WithErrorAndLoading>
      </DialogContent>
    </Dialog>
  );
}